import { CellClassParams, ColDef, KeyCreatorParams, ValueFormatterParams } from "ag-grid-community";

import CableRenderer from "fond/topologicalView/CableRenderer";
import FiberCellRenderer, { formatFiberRange } from "fond/topologicalView/FiberCellRenderer";
import { formatSpliceRecordCable } from "fond/topologicalView/formatSpliceRecordCable";
import HandleRenderer from "fond/topologicalView/HandleRenderer";
import LeafRenderer from "fond/topologicalView/LeafRenderer";
import { SpliceRecord, SpliceRecordCable, TopologicalNodeData } from "fond/topologicalView/types";

// AG Grid's default column width, used when a column doesn't specify one.
const DEFAULT_COLUMN_WIDTH = 200;

// Columns sized to fit their content, from the widths estimated when processing the data.
const CONTENT_SIZED_FIELDS = new Set(["input", "output", "leaf"]);

export const gridOptions = {
  rowGroupPanelShow: "never" as const,
  sideBar: false,
  pagination: false,
  suppressMovableColumns: true,
  domLayout: "autoHeight" as const,
  defaultColDef: {
    sortable: true,
    hide: true,
    resizable: true,
  },
  enableCellTextSelection: true,
  ensureDomOrder: true,
};

const spliceRecordCableFormatter = (params: ValueFormatterParams<SpliceRecord, SpliceRecordCable>) => {
  return formatSpliceRecordCable({ value: params.value, recordCount: params.data?.recordCount });
};

const styleCableCell = (params: CellClassParams<SpliceRecord, SpliceRecordCable>) => {
  if (params.value === "dead" || params.value === "spare") {
    return null;
  }
  const backgroundColor = params.value?.cable.backgroundColor ?? null;
  if (backgroundColor) {
    return { backgroundColor };
  } else {
    return null;
  }
};

/**
 * Build the column definitions for a splice range node's grid.
 */
export const buildColumnDefs = ({
  hasLeafRow,
  columnWidths,
}: {
  hasLeafRow: boolean;
  columnWidths: TopologicalNodeData["columnWidths"];
}): ColDef<SpliceRecord>[] => [
  {
    field: "input.handleId",
    headerName: "",
    hide: false,
    cellRenderer: HandleRenderer,
    cellRendererParams: { type: "target" },
    width: 15,
    maxWidth: 15,
    minWidth: 15,
    lockVisible: true,
    suppressColumnsToolPanel: true,
    suppressHeaderMenuButton: true,
  },
  {
    field: "input",
    headerName: "Cable In",
    cellRenderer: CableRenderer,
    cellRendererParams: { side: "input" },
    valueFormatter: spliceRecordCableFormatter,
    width: columnWidths.cableIn,
    minWidth: columnWidths.cableIn,
    lockVisible: true,
    hide: false,
    cellStyle: styleCableCell,
    filter: "agSetColumnFilter",
    filterParams: { valueFormatter: spliceRecordCableFormatter },
    flex: 1,
  },
  { field: "input.tier", width: 125, minWidth: 125, maxWidth: 125, filter: "agTextColumnFilter" },
  {
    field: "input.fiberRange",
    headerName: "Fiber In",
    width: 125,
    minWidth: 125,
    lockVisible: true,
    hide: false,
    cellRenderer: FiberCellRenderer,
    cellRendererParams: { side: "input" },
    valueFormatter: (params) => formatFiberRange(params.value),
  },
  {
    field: "spliceType",
    headerName: "Splice Type",
    filter: true,
  },
  {
    field: "output",
    headerName: "Cable Out",
    cellRenderer: CableRenderer,
    cellRendererParams: { side: "output" },
    valueFormatter: spliceRecordCableFormatter,
    width: columnWidths.cableOut,
    minWidth: columnWidths.cableOut,
    lockVisible: true,
    hide: false,
    cellStyle: styleCableCell,
    filter: "agSetColumnFilter",
    filterParams: { valueFormatter: spliceRecordCableFormatter },
    flex: 1,
  },
  { field: "output.tier" },
  {
    field: "output.fiberRange",
    headerName: "Fiber Out",
    width: 125,
    minWidth: 125,
    lockVisible: true,
    hide: false,
    cellRenderer: FiberCellRenderer,
    cellRendererParams: { side: "output" },
    valueFormatter: (params) => formatFiberRange(params.value),
  },
  {
    field: "leaf",
    headerName: "Service Location",
    cellRenderer: LeafRenderer,
    width: columnWidths.leaf,
    minWidth: columnWidths.leaf,
    // Only show the 'Service Location' column by default if there are leaf children.
    hide: !hasLeafRow,
    filter: "agSetColumnFilter",
    filterParams: {
      keyCreator: (params: KeyCreatorParams<SpliceRecord, { label: string }>) => params.value?.label,
      valueFormatter: (params: ValueFormatterParams<SpliceRecord, { label: string }>) => params.value?.label,
    },
    valueFormatter: (params) => params.value?.label,
  },
  {
    field: "output.handleId",
    headerName: "",
    cellRenderer: HandleRenderer,
    cellRendererParams: { type: "source" },
    hide: false,
    width: 15,
    maxWidth: 15,
    minWidth: 15,
    suppressColumnsToolPanel: true,
    suppressHeaderMenuButton: true,
    lockVisible: true,
  },
];

/**
 * Gets the combined width of the columns shown by default, excluding those sized to fit their content.
 */
export const getFixedColumnsWidth = (): number =>
  buildColumnDefs({ hasLeafRow: false, columnWidths: { cableIn: 0, cableOut: 0, leaf: 0 } })
    .filter((column) => !(column.hide ?? gridOptions.defaultColDef.hide))
    .filter((column) => !CONTENT_SIZED_FIELDS.has(column.field ?? ""))
    .reduce((total, column) => total + (column.width ?? DEFAULT_COLUMN_WIDTH), 0);
